import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { AdminNeoChat } from '../components/AdminNeoChat';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate } from '../format';
import { apiErrorMessage, stateLabel, type NeoChange } from '../neoChanges';
import { GuideLink } from '../evals/GuideLink';

// Neo → Changes (ADR 0231). Product changes admins asked Neo for, and the chat
// that drafts them. The list reads cloud's own record only; a change's page
// reads its live status from GitHub.

interface ChangeList {
  configured: boolean;
  changes: NeoChange[];
}

export function AdminNeoChanges() {
  const [data, setData] = useState<ChangeList | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    adminApi<ChangeList>('/v1/admin/neo/changes')
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (!ignore) setError(apiErrorMessage(err, 'Failed to load changes.'));
      });
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Product changes</h1>
        {data !== null && <span className="admin-count">{data.changes.length}</span>}
        <GuideLink section="change-neo" />
      </header>
      <p className="admin-hint">
        Ask Neo to change the product. A coding agent makes the change as a pull request; CI checks
        it; you merge it from the change&apos;s page, and the merge deploys to production.
      </p>
      {data !== null && !data.configured && (
        <div className="admin-auth-banner">
          Neo changes are not configured on this server: the tombolo-neo GitHub App is not set up
          (see the admin console docs, &quot;Neo changes&quot;). You can still talk to Neo, but
          nothing can start.
        </div>
      )}
      {error !== null && <p className="admin-inline-error">{error}</p>}

      <div className="neo-change-layout">
        <AdminNeoChat configured={data?.configured ?? false} />

        <div className="neo-change-list">
          <h2>Recent changes</h2>
          {data === null && error === null && <p className="admin-loading">Loading…</p>}
          {data !== null && data.changes.length === 0 && (
            <p className="admin-empty">No changes yet.</p>
          )}
          {data !== null && data.changes.length > 0 && (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Change</th>
                    <th>State</th>
                    <th>PR</th>
                    <th>Asked by</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.changes.map((c) => {
                    const state = stateLabel(c.state);
                    return (
                      <tr key={c.id}>
                        <td>
                          <Link to={`/admin/neo/changes/${c.id}`}>{c.title}</Link>
                        </td>
                        <td>
                          <StatusBadge status={state.badge} label={state.label} />
                        </td>
                        <td>{c.prNumber === null ? '—' : `#${c.prNumber}`}</td>
                        <td>{c.requestedBy ?? '—'}</td>
                        <td>{formatDate(c.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
